/* eslint-disable no-case-declarations */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Container } from '../../../../ui/gridSystem';
import { Icon, ProgressBar, Text } from '../../../../ui/atoms';
import {
  AvarageBox,
  Card,
  CardsGrid,
  OpenAnswer,
  Question,
  RatingAnswer,
} from './style';
import { withMediaQueries } from '../../../hoc/withMediaQueries';
import CircularStats from '../../../../ui/atoms/CircularStats';
import { generateColorPalette } from '../../../../utils/colors';
import {
  SURVEY_FREE_TEXT_ANSWERS_GET,
  SURVEY_STATS_GET,
} from '../../../redux/actions/statistics';
import { starsOptions } from '../../../../ui/atoms/Modals/ModalSurvey';
import Pagination from '../../../../ui/atoms/Pagination';

const renderBox = ({ data, index, survey, callback }) => {
  const chartData = {};
  const colors = generateColorPalette(starsOptions?.length);

  switch (data?.answer_type) {
    case 'rating':
      if (!data.total_answers > 0) return null;
      const average = (
        (data?.stats || []).reduce((acc, v) => (acc += v.count * v.label), 0) /
        data.total_answers
      ).toFixed(1);
      return (
        <Card span={8} key={`card-${index}`}>
          <Question>
            <Text type="h2.5">{index}</Text>
            <Text type="h3" mb={24} className="question">
              {data?.question}
            </Text>
          </Question>
          <div className="d-flex g-24">
            <AvarageBox>
              <Text type="b5" mb={16}>
                Punteggio complessivo
              </Text>
              <div className="d-flex">
                <Icon
                  type="star-01"
                  size={94}
                  stroke="##0088E3"
                  fill="#0088E3"
                  clickable={false}
                />
                <div>
                  <Text type="b1">{average}</Text>
                  <Text type="b4">
                    {data?.total_answers || 0}&nbsp;risposte
                  </Text>
                </div>
              </div>
            </AvarageBox>
            <div className="stars-wrapper">
              {(starsOptions || []).map((a, i) => {
                const starStats = data?.stats?.find(
                  (opt) => opt?.label?.toString() === a?.value?.toString()
                );
                return (
                  <RatingAnswer key={`ans-${i}`}>
                    <Text type="b5">{a?.label}</Text>
                    <Text type="b5">{a?.value}</Text>
                    <Icon
                      type="star-01"
                      stroke={colors[starsOptions.length - 1 - i]}
                      fill={colors[starsOptions.length - 1 - i]}
                      clickable={false}
                    />
                    <div className="progressbar-wrapper">
                      <ProgressBar
                        color={colors[starsOptions.length - 1 - i]}
                        bgColor="#EEEEEE"
                        height={24}
                        percentage={
                          (100 * (starStats?.count || 0)) / data.total_answers
                        }
                      />
                    </div>
                    <Text type="b5">{starStats?.count || 0}</Text>
                    <Text type="b5">
                      {Math.round(
                        (100 * (starStats?.count || 0)) / data.total_answers
                      )}
                      %
                    </Text>
                  </RatingAnswer>
                );
              })}
            </div>
          </div>
        </Card>
      );
    case 'id':
      // put together answers (labels) & stats (count)
      const fullData = (data?.answers || []).map((ans) => {
        const stat = (data?.stats || []).find(
          (s) => s?.label === ans?.id?.toString()
        );
        return { ...ans, count: Number(stat?.count || 0) };
      });
      // format data for CircularStats
      (fullData || []).forEach((a) => {
        chartData[a?.value] = a?.count;
      });
      return (
        <Card span={6} key={`card-${index}`}>
          <Question>
            <Text type="h2.5">{index}</Text>
            <Text type="h3" mb={24} className="question">
              {data?.question}
            </Text>
          </Question>
          <CircularStats label="Risposte totali" size={180} data={chartData} />
        </Card>
      );
    case 'free_text':
      const qId = data?.question_id?.toString();
      if (!qId || !survey[qId]) return null;
      const { answers, pager } = survey[qId];
      return (
        <Card span={12} key={`card-${index}`}>
          <Question>
            <Text type="h2.5">{index}</Text>
            <Text type="h3" mb={24} className="question">
              {data?.question}
            </Text>
          </Question>
          <Text type="b5" mb={8}>
            {data?.total_answers} risposte
          </Text>
          {(answers || []).map((a, i) => (
            <OpenAnswer key={`open-ans-${i}`}>{a?.value}</OpenAnswer>
          ))}
          {survey[qId]?.pager?.number_of_pages > 1 && (
            <Pagination pager={pager} callBack={callback} />
          )}
        </Card>
      );

    default:
      return null;
  }
};

const Survey = ({ data, getSurveyStats, getSurveyFreeTextAnswers }) => {
  const { course_id } = useParams();
  const { general, statistics } = data;

  useEffect(() => {
    getSurveyStats(course_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_id]);

  return (
    <Container className="pt-0">
      <Text type="h2" mb={34}>
        Report survey
      </Text>
      <CardsGrid>
        {/* BOX 1: info generali */}
        {general?.users_answered > 0 ? (
          <>
            <Card span={4}>
              <Text type="h2" mb={24}>
                Utenti che hanno risposto
              </Text>
              <Text type="b1" mb={29}>
                <span className="">{general?.users_answered || 0}</span>/
                {general.total_users}
              </Text>
              <Text type="b2" mb={12} className="text-right">
                {Math.round(
                  (100 * (general?.users_answered || 0)) / general.total_users
                )}
                %
              </Text>
              <ProgressBar
                percentage={Math.round(
                  (100 * (general?.users_answered || 0)) / general.total_users
                )}
                color="#0088E3"
                bgColor="#EEEEEE"
                height={24}
              />
            </Card>
            {(statistics || []).map((question, index) =>
              renderBox({
                data: question,
                index: index + 1,
                survey: data,
                callback: (page) =>
                  getSurveyFreeTextAnswers(
                    course_id,
                    question?.question_id,
                    page
                  ),
              })
            )}
          </>
        ) : (
          <Text type="b3">Non sono presenti statistiche per questo corso</Text>
        )}
      </CardsGrid>
    </Container>
  );
};

Survey.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  // HOC: dispatch
  getSurveyStats: PropTypes.func.isRequired,
  getSurveyFreeTextAnswers: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { survey: data } = state.statistics.course;
    return {
      data,
    };
  },
  (dispatch) => ({
    getSurveyStats: (id) => dispatch({ type: SURVEY_STATS_GET._REQUEST, id }),
    getSurveyFreeTextAnswers: (courseId, questionId, page) =>
      dispatch({
        type: SURVEY_FREE_TEXT_ANSWERS_GET._REQUEST,
        courseId,
        questionId,
        page,
      }),
  })
)(withMediaQueries(Survey));
